/**
 * TSS Shared Authentication Utilities
 *
 * This module wraps AWS Amplify Auth for Cognito integration.
 *
 * Features:
 * - Email/password authentication via Cognito User Pool
 * - OAuth authentication (Google, Apple) via Cognito Identity Providers
 * - Email verification flow (sign up -> confirm code)
 * - JWT token retrieval for API authorization
 * - Parental consent flow for minors (COPPA compliance)
 * - Cross-domain session sharing via cookie storage
 */

import { Amplify } from 'aws-amplify';
import { cognitoUserPoolsTokenProvider } from 'aws-amplify/auth/cognito';
import {
  signIn,
  signUp,
  signOut,
  confirmSignUp,
  getCurrentUser,
  fetchAuthSession,
  signInWithRedirect,
  resetPassword,
  confirmResetPassword,
  type SignInInput,
  type SignUpInput,
  type ResetPasswordInput,
  type ConfirmResetPasswordInput,
} from 'aws-amplify/auth';
import { CookieStorage } from 'aws-amplify/utils';
import { getConfig } from '../config';

/**
 * Get the parent domain for cookie storage
 * This allows sharing auth tokens across subdomains (coach/athlete portals)
 * e.g., coach.itsross.com and athlete.itsross.com share .itsross.com cookies
 */
function getParentDomain(): string {
  if (typeof window === 'undefined') return '';

  const hostname = window.location.hostname;

  // localhost doesn't need parent domain
  if (hostname === 'localhost' || hostname === '127.0.0.1') {
    return hostname;
  }

  // Extract parent domain (e.g., coach.itsross.com -> .itsross.com)
  const parts = hostname.split('.');
  if (parts.length >= 2) {
    // Return last two parts with leading dot for subdomain cookie sharing
    return '.' + parts.slice(-2).join('.');
  }

  return hostname;
}

// Configure Amplify with OAuth support and cookie storage for cross-domain auth
export function configureAuth() {
  const config = getConfig();
  const parentDomain = getParentDomain();
  const isSecure = typeof window !== 'undefined' && window.location.protocol === 'https:';

  const authConfig: Record<string, unknown> = {
    Auth: {
      Cognito: {
        userPoolId: config.cognito.userPoolId,
        userPoolClientId: config.cognito.userPoolClientId,
      },
    },
  };

  // Add OAuth configuration if Cognito domain is configured
  if (config.cognito.domain) {
    (authConfig.Auth as Record<string, unknown>).Cognito = {
      ...(authConfig.Auth as Record<string, Record<string, unknown>>).Cognito,
      loginWith: {
        oauth: {
          domain: config.cognito.domain,
          scopes: ['email', 'openid', 'profile'],
          redirectSignIn: [config.oauth.redirectSignIn],
          redirectSignOut: [config.oauth.redirectSignOut],
          responseType: 'code',
        },
      },
    };
  }

  Amplify.configure(authConfig);

  // Configure cookie storage for cross-domain session sharing
  // This allows coach and athlete portals to share the same auth session
  cognitoUserPoolsTokenProvider.setKeyValueStorage(
    new CookieStorage({
      domain: parentDomain,
      path: '/',
      expires: 30, // 30 days
      secure: isSecure,
      sameSite: 'lax',
    })
  );
}

// Sign up a new user
export async function register(
  email: string,
  password: string,
  options?: {
    givenName?: string;
    familyName?: string;
    birthDate?: string;
    parentEmail?: string;
    inviteToken?: string;
  }
) {
  const userAttributes: Record<string, string> = { email };

  // Add name attributes if provided
  if (options?.givenName) {
    userAttributes.given_name = options.givenName;
  }
  if (options?.familyName) {
    userAttributes.family_name = options.familyName;
  }
  if (options?.birthDate) {
    userAttributes.birthdate = options.birthDate;
  }

  // Build sign up options
  const signUpOptions: SignUpInput['options'] = {
    userAttributes,
  };

  // Pass invite token as client metadata for PreSignUp Lambda to auto-confirm
  if (options?.inviteToken) {
    signUpOptions.clientMetadata = {
      invite_token: options.inviteToken,
    };
  }

  const input: SignUpInput = {
    username: email,
    password,
    options: signUpOptions,
  };

  const result = await signUp(input);

  // If this is a minor requiring parental consent, notify the backend
  if (options?.parentEmail) {
    const config = getConfig();
    try {
      const response = await fetch(`${config.api.endpoint}/api/v1/parental-consent/request`, {
        method: 'POST',
        cache: 'no-store',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          childEmail: email,
          parentEmail: options.parentEmail,
          birthDate: options.birthDate,
          givenName: options.givenName,
          familyName: options.familyName,
        }),
      });
      if (!response.ok) {
        console.error('Failed to send parental consent request');
      }
    } catch (err) {
      console.error('Failed to send parental consent request:', err);
    }
  }

  return result;
}

// Confirm sign up with verification code
export async function confirmRegistration(email: string, code: string) {
  return confirmSignUp({
    username: email,
    confirmationCode: code,
  });
}

// Sign in
export async function login(email: string, password: string) {
  const input: SignInInput = {
    username: email,
    password,
  };
  return signIn(input);
}

// Sign out
export async function logout() {
  return signOut();
}

// Get current authenticated user
export async function getUser() {
  try {
    const user = await getCurrentUser();
    return user;
  } catch {
    return null;
  }
}

// Get auth token for API calls
export async function getAuthToken(): Promise<string | null> {
  try {
    const session = await fetchAuthSession();
    return session.tokens?.idToken?.toString() || null;
  } catch {
    return null;
  }
}

// Check if user is authenticated
export async function isAuthenticated(): Promise<boolean> {
  const user = await getUser();
  return user !== null;
}

// Sign in with Google OAuth
export async function signInWithGoogle() {
  const config = getConfig();
  if (!config.cognito.domain) {
    throw new Error('OAuth not configured. Please set VITE_COGNITO_DOMAIN.');
  }
  return signInWithRedirect({ provider: 'Google' });
}

// Sign in with Apple OAuth
export async function signInWithApple() {
  const config = getConfig();
  if (!config.cognito.domain) {
    throw new Error('OAuth not configured. Please set VITE_COGNITO_DOMAIN.');
  }
  return signInWithRedirect({ provider: 'Apple' });
}

// Check if OAuth providers are available
export function getAvailableOAuthProviders(): { google: boolean; apple: boolean } {
  const config = getConfig();
  return {
    google: config.oauth.providers.google && !!config.cognito.domain,
    apple: config.oauth.providers.apple && !!config.cognito.domain,
  };
}

// Request password reset - sends verification code to email
export async function requestPasswordReset(email: string) {
  const input: ResetPasswordInput = {
    username: email,
  };
  return resetPassword(input);
}

// Confirm password reset with verification code and new password
export async function confirmPasswordReset(email: string, code: string, newPassword: string) {
  const input: ConfirmResetPasswordInput = {
    username: email,
    confirmationCode: code,
    newPassword,
  };
  return confirmResetPassword(input);
}
